import React, { useId } from "react";
import { Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { Check, FileText, Upload, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/** Icon tile + step title + one-line description at the top of each onboarding step. */
export function StepHeader({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return (
    <div className="mb-8 flex items-center gap-4">
      <div
        className="flex size-11 shrink-0 items-center justify-center rounded-sm border border-border bg-card text-foreground [&_svg]:size-5"
        aria-hidden
      >
        {icon}
      </div>
      <div className="min-w-0">
        <h1 className="text-display-sm text-foreground">{title}</h1>
        <p className="mt-0.5 text-sm text-body">{description}</p>
      </div>
    </div>
  );
}

/** One required document: label + format hint, and either an upload control or the chosen file. */
export function DocumentUploadRow({
  label,
  hint,
  icon,
  file,
  onSelect,
  onClear,
}: {
  label: string;
  hint: string;
  icon: React.ReactNode;
  file: File | null | undefined;
  onSelect: (file: File) => void;
  onClear: () => void;
}) {
  const inputId = useId();
  return (
    <div
      className={cn(
        "flex flex-col gap-3 rounded-md border p-4 transition-colors sm:flex-row sm:items-center sm:justify-between",
        file ? "border-success/30 bg-success/5" : "border-border bg-card hover:border-hairline-strong",
      )}
    >
      <div className="flex min-w-0 items-center gap-3">
        <div
          className={cn(
            "flex size-9 shrink-0 items-center justify-center rounded-sm border bg-background [&_svg]:size-4",
            file ? "border-success/30 text-success" : "border-border text-foreground",
          )}
          aria-hidden
        >
          {file ? <Check /> : icon}
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium text-foreground">{label}</p>
          <p className="text-caption text-muted-foreground">{hint}</p>
        </div>
      </div>

      {file ? (
        <div className="flex min-w-0 items-center gap-1 self-start rounded-sm border border-border bg-background py-0.5 pr-0.5 pl-2 sm:self-auto">
          <FileText className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
          <span className="max-w-[180px] truncate font-mono text-caption text-body">{file.name}</span>
          <button
            type="button"
            onClick={onClear}
            aria-label={`Remove ${file.name}`}
            className="rounded-xs p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <X className="size-3" />
          </button>
        </div>
      ) : (
        <label
          htmlFor={inputId}
          className="inline-flex h-8 shrink-0 cursor-pointer items-center justify-center gap-2 self-start rounded-sm border border-border px-3 text-[13px] font-medium text-foreground transition-colors focus-within:ring-2 focus-within:ring-ring hover:bg-accent sm:self-auto [&_svg]:size-4"
        >
          <Upload aria-hidden />
          Upload file
          <input
            id={inputId}
            type="file"
            className="sr-only"
            aria-label={`Upload ${label}`}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onSelect(f);
            }}
          />
        </label>
      )}
    </div>
  );
}

/** Completion card shown after onboarding is submitted. */
export function OnboardingComplete({
  title,
  body,
  status,
  to,
  cta,
}: {
  title: string;
  body: string;
  status: string;
  to: "/partner/dashboard" | "/student/dashboard";
  cta: string;
}) {
  return (
    <div className="w-full rounded-md border border-border bg-card p-6 text-center md:p-10">
      <motion.div
        initial={{ scale: 0.6, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.4, type: "spring" }}
        className="mx-auto mb-6 flex size-14 items-center justify-center rounded-full border border-success/30 bg-success/10 text-success"
      >
        <Check className="size-6" />
      </motion.div>

      <h1 className="text-display-sm text-foreground">{title}</h1>
      <p className="mx-auto mt-2 max-w-sm text-sm leading-6 text-body">{body}</p>

      <div className="mx-auto mt-6 mb-6 flex max-w-sm items-center justify-center gap-2 rounded-sm border border-border bg-background px-3 py-2.5">
        <span className="size-1.5 animate-pulse rounded-full bg-success" aria-hidden />
        <span className="text-caption text-body">{status}</span>
      </div>

      <Button asChild size="lg" className="w-full">
        <Link to={to}>{cta}</Link>
      </Button>
    </div>
  );
}
